import { useQuery } from 'urql';

import {
  type CustomRoleTaxonomySelection,
  ROLE_TAXONOMY_VERSIONS_QUERY,
} from '@revelio/data-access';

/**
 * Legacy role taxonomy bootstrap (GraphQL `ROLE_TAXONOMY_VERSIONS_QUERY` only).
 * Used by `FilterProvider` when `FeatureFlag.ReferenceApi` is off. Prefer
 * `shared/selection-lists/hooks/useRoleTaxonomyAdapter` when the reference API
 * path is enabled.
 */
export const useRoleTaxonomyVersions = () => {
  const [{ data: roleTaxonomyVersions, fetching, error }] = useQuery({
    query: ROLE_TAXONOMY_VERSIONS_QUERY,
  });

  return {
    taxonomies: roleTaxonomyVersions?.roleTaxonomies,
    loading: fetching,
    error,
  };
};

export const useCurrentRoleTaxonomyVersion = () => {
  const { taxonomies, loading, error } = useRoleTaxonomyVersions();

  const defaultVersion = (() => {
    const latestDefaultTaxonomy = taxonomies?.find(
      (taxonomy) => taxonomy.taxonomyName === 'default' && taxonomy.isLatest
    );
    if (latestDefaultTaxonomy) return latestDefaultTaxonomy.version;

    const latestTaxonomy = taxonomies?.find((taxonomy) => taxonomy.isLatest);
    if (latestTaxonomy) return latestTaxonomy.version;

    return undefined;
  })();

  const getSpecificTaxonomyVersion = (
    taxonomySelection: CustomRoleTaxonomySelection
  ) => {
    if (taxonomySelection.default) return defaultVersion;

    if (taxonomySelection.ricsK10Id) {
      const taxonomySelectionValue = taxonomySelection.ricsK10Id;
      const taxonomyName = `rics_k10_${taxonomySelectionValue}`;
      const taxonomy = taxonomies?.find(
        (taxonomy) => taxonomy.taxonomyName === taxonomyName
      );
      return taxonomy?.version;
    }

    if (taxonomySelection.ricsK50Id) {
      const taxonomySelectionValue = taxonomySelection.ricsK50Id;
      const taxonomyName = `rics_k50_${taxonomySelectionValue}`;
      const taxonomy = taxonomies?.find(
        (taxonomy) => taxonomy.taxonomyName === taxonomyName
      );
      return taxonomy?.version;
    }

    return undefined;
  };

  return {
    version: defaultVersion,
    taxonomies,
    loading: loading || (!taxonomies && !error),
    error,
    getSpecificTaxonomyVersion,
  };
};
